import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useCRM } from './CRMContext';
import { systemAPI } from '../utils/crmApi';

const defaultConfig = {
  banner: { enabled: false, message: '', severity: 'info' },
  maintenance: { enabled: false, message: '' }
};

const SystemConfigContext = createContext({
  banner: defaultConfig.banner,
  maintenance: defaultConfig.maintenance,
  loading: false,
  error: null,
  refresh: () => {}
});

export const SystemConfigProvider = ({ children }) => {
  const { token } = useCRM();
  const [banner, setBanner] = useState(defaultConfig.banner);
  const [maintenance, setMaintenance] = useState(defaultConfig.maintenance);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) {
      setBanner(defaultConfig.banner);
      setMaintenance(defaultConfig.maintenance);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await systemAPI.getConfig();
      if (response.data?.success) {
        const data = response.data.data || {};
        setBanner(data.banner || defaultConfig.banner);
        setMaintenance(data.maintenance || defaultConfig.maintenance);
      } else {
        setBanner(defaultConfig.banner);
        setMaintenance(defaultConfig.maintenance);
      }
    } catch (err) {
      console.error('Failed to load system configuration:', err);
      setError(err);
      setBanner(defaultConfig.banner);
      setMaintenance(defaultConfig.maintenance);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({
    banner,
    maintenance,
    loading,
    error,
    refresh
  }), [banner, maintenance, loading, error, refresh]);

  return (
    <SystemConfigContext.Provider value={value}>
      {children}
    </SystemConfigContext.Provider>
  );
};

export const useSystemConfig = () => {
  const ctx = useContext(SystemConfigContext);
  if (!ctx) {
    throw new Error('useSystemConfig must be used within a SystemConfigProvider');
  }
  return ctx;
};


